/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { useServiceContext } from '../../../root'
import { RcmTableDataWrapper } from '../../../components/rcm-table/RcmTableDataWrapper'
import { useRcmTableData } from '../../../hooks/useRcmTableData'
import { RcmTable } from '../../../components/rcm-table/RcmTable'
import { TableHeadRow, TableHeadCell, TableRow } from '../../../components/table/TableComponents'
import TextLinkButton from '../../../components/shared/TextLinkButton'
import DocIcon from '../../../components/shared/DocIcon'
import DocState from '../../../components/shared/DocState'
import { AclLevel, FsiType } from '../../../constants/fsi'
import { EtmfListView, type EtmfListViewValue } from '../../../constants/etmf'
import RcmTableFilterTrigger from '../../../components/rcm-table/RcmTableFilterTrigger'
import { EtmfListFilter, type EtmfListFilterRef } from './EtmfListFilter'
import type { QueryParams } from '../../../services/api/base'
import type { MasterDataItem, FilePlanItem } from '../../../services/api/etmf'
import { removePackage, type Package } from '../../../services/api/packages'
import { createDateTimeFormatter } from '../../../utils/commonColumns'
import ActionsMenu from '../../../components/shared/ActionsMenu'
import { getPackagesActions } from './EtmfActions'
import FilePlansTableMenu from './FilePlansTableMenu'
import EtmfPageMenu from './EtmfPageMenu'
import { EtmfActionsMenu } from './EtmfActionsMenu'
import LoadingSpinner from '../../../components/LoadingSpinner'
import { useModal } from '~/contexts/ModalContext'
import EditPackageModal from '~/components/etmf/edit-package-modal'
import ModalConfirmation from '~/components/shared/ModalConfirmation'
import HtmlCell from '~/components/HtmlCell.tsx'
import ApplyPackageModal from '~/components/etmf/apply-package/ApplyPackageModal'
import FsiPropertiesModal from '~/pages/browser/properties/FsiPropertiesModal'

interface EtmfListPageProps {
  listView: EtmfListViewValue
}

interface PageTexts {
  title: string
  filterLabel: string
  addNewMDF: string
}

export const EtmfListPage: React.FC<{ listView: EtmfListViewValue }> = ({ listView }) => {
  const context = useServiceContext()
  const {
    service: {
      filter: { translate },
      checkmate
    },
    api
  } = context

  const persistenceKey = `etmfList_${listView}`

  const pageTextsMap: Record<EtmfListViewValue, PageTexts> = {
    [EtmfListView.PRODUCT]: {
      title: 'EMTF_MD_PRODUCTS',
      filterLabel: 'EMTF_MD_FILTER_PRODUCT_NAME',
      addNewMDF: 'ETMF_ACTION_ADD_NEW_PRODUCT'
    },
    [EtmfListView.SITE]: {
      title: 'EMTF_MD_SITES',
      filterLabel: 'EMTF_MD_FILTER_SITE_NAME',
      addNewMDF: 'ETMF_ACTION_ADD_NEW_SITE'
    },
    [EtmfListView.COUNTRY]: {
      title: 'EMTF_MD_COUNTRIES',
      filterLabel: 'EMTF_MD_FILTER_COUNTRY_NAME',
      addNewMDF: 'ETMF_ACTION_ADD_NEW_COUNTRY'
    },
    [EtmfListView.ENVIRONMENT]: {
      title: 'EMTF_MD_ENVIRONMENTS',
      filterLabel: 'EMTF_MD_FILTER_ENVIRONMENT_NAME',
      addNewMDF: 'ETMF_ACTION_ADD_NEW_ENVIRONMENT'
    },
    [EtmfListView.TRIAL]: {
      title: 'EMTF_MD_TRIALS',
      filterLabel: 'EMTF_MD_FILTER_TRIAL_NAME',
      addNewMDF: 'ETMF_ACTION_ADD_NEW_STUDY'
    },
    [EtmfListView.STUDY]: {
      title: 'EMTF_MD_TRIALS',
      filterLabel: 'EMTF_MD_FILTER_TRIAL_NAME',
      addNewMDF: 'ETMF_ACTION_ADD_NEW_STUDY'
    },
    [EtmfListView.FILE_PLAN]: {
      title: 'EMTF_FILEPLANS',
      filterLabel: 'EMTF_MD_FILTER_FILE_PLAN_NAME',
      addNewMDF: 'ETMF_ACTION_ADD_NEW_FILE_PLAN'
    },
    [EtmfListView.PACKAGE]: {
      title: 'EMTF_PACKAGES_TITLE',
      filterLabel: '',
      addNewMDF: 'ETMF_PACKAGES_ADD_NEW_PACKAGE_ACTION'
    }
  }

  const pageTexts: PageTexts = pageTextsMap[listView] || pageTextsMap[EtmfListView.PRODUCT]

  const loadData = useCallback(
    (queryParams: QueryParams, filter: Record<string, unknown>) => {
      return new Promise<{ elements: (MasterDataItem | FilePlanItem | Package)[]; totalElements: number }>(
        (resolve, reject) => {
          // Combine and clean filter params
          const combinedParams = { ...filter, ...queryParams }
          const cleanedParams: QueryParams = {}

          // Only include valid QueryParams types
          Object.entries(combinedParams).forEach(([key, value]) => {
            if (value === undefined || value === null) return
            if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
              cleanedParams[key] = value
            } else if (Array.isArray(value)) {
              const arr = value.filter((v) => typeof v === 'string' || typeof v === 'number')
              if (arr.length > 0) {
                cleanedParams[key] = arr as string[] | number[]
              }
            }
          })

          const apiCall = (() => {
            if (listView === EtmfListView.FILE_PLAN) {
              return api.etmf.getFilePlans(cleanedParams)
            } else if (listView === EtmfListView.PACKAGE) {
              return api.packages.getPackagesList(cleanedParams)
            } else {
              return api.etmf.getMasterData(listView, cleanedParams)
            }
          })()

          apiCall.then(
            (data) => {
              resolve({
                elements:
                  listView === EtmfListView.FILE_PLAN && Array.isArray(data.elements)
                    ? data.elements.map((file: any) => ({
                        ...file,
                        isFilePlan: true
                      }))
                    : data.elements,
                totalElements: data.totalElements
              })
            },
            (error) => {
              reject(error)
            }
          )
        }
      )
    },
    [listView, api]
  )

  const showPageMenu =
    ([EtmfListView.PRODUCT, EtmfListView.FILE_PLAN, EtmfListView.PACKAGE] as EtmfListViewValue[]).includes(listView) ||
    !checkmate.isCheckmated()

  return (
    <div className="mcc-content-frame theme-sandman">
      <RcmTableDataWrapper
        loadData={loadData}
        defaultSortBy={'name'}
        defaultSortDesc={false}
        persistenceKey={persistenceKey}
      >
        <EtmfListPageContent listView={listView} pageTexts={pageTexts} showPageMenu={showPageMenu} />
      </RcmTableDataWrapper>
    </div>
  )
}

// Inner component that has access to RcmTableDataContext
const EtmfListPageContent: React.FC<{
  listView: EtmfListViewValue
  pageTexts: PageTexts
  showPageMenu: boolean
}> = ({ listView, pageTexts, showPageMenu }) => {
  const context = useServiceContext()
  const {
    service: {
      filter: { translate },
      access
    }
  } = context
  const tableData = useRcmTableData()

  // Listen for global table refresh events (legacy parity)
  useEffect(() => {
    const handler = (event: Event) => {
      if ((event as CustomEvent).type === 'bctable:updateTable') {
        tableData.refreshTable()
      }
    }
    window.addEventListener('bctable:updateTable', handler)
    return () => {
      window.removeEventListener('bctable:updateTable', handler)
    }
  }, [tableData])
  const navigate = useNavigate()
  const { showModal } = useModal()
  const formatDateTime = createDateTimeFormatter(context.service.auth, translate)
  const eventTriggers = context.service.init.getFeaturesInfo()?.eventTriggers || []
  const filterRef = useRef<EtmfListFilterRef>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)

  // Function to refresh all page data (filter options and table)
  const refreshAllData = async () => {
    setIsRefreshing(true)
    try {
      // Refresh filter options if package view
      if (listView === EtmfListView.PACKAGE && filterRef.current) {
        await filterRef.current.refreshPackageOptions()
      }
      // Refresh table data
      tableData.refreshTable()
    } finally {
      setIsRefreshing(false)
    }
  }

  const renderMDFTable = () => (
    <RcmTable
      className="chita-table"
      onRowEnter={(fsi: MasterDataItem) => {
        console.log('MDF row clicked:', { id: fsi.id, type: listView })
      }}
      HeadRow={
        <TableHeadRow className={'has-actions'}>
          <TableHeadCell sort={'name'}>{translate('NAME')}</TableHeadCell>
          <TableHeadCell id="58ae1e98-0ab7-4765-94b0-6dcde9abe032" className={'actions'}>
            {translate('ACTIONS')}
          </TableHeadCell>
        </TableHeadRow>
      }
      Row={(props: { row: MasterDataItem; [key: string]: any }) => {
        const fsi = props.row
        return (
          <TableRow {...props}>
            <td className="clipped-cell">
              <div className="d-flex align-left">
                <TextLinkButton
                  className="ellipsis"
                  title={fsi.name}
                  action={() => {
                    showModal(
                      <FsiPropertiesModal
                        fileId={fsi?.id}
                        isMDF={true}
                        MDFType={fsi?.MDFType}
                        isModal={true}
                        fsiDetails={fsi}
                      />
                    )
                  }}
                >
                  {fsi.name}
                </TextLinkButton>
              </div>
            </td>
            <td className={'actions'}>
              <EtmfActionsMenu item={fsi} listView={listView} />
            </td>
          </TableRow>
        )
      }}
    />
  )

  const renderFilePlansTable = () => (
    <RcmTable
      className="chita-table"
      onRowEnter={(fsi: FilePlanItem) => {
        if (fsi.type !== FsiType.PLACEHOLDER && fsi.id && fsi.acl?.level !== AclLevel.BROWSE) {
          console.log('Navigate to file:', fsi.id)
        }
      }}
      HeadRow={
        <TableHeadRow className={'has-actions'}>
          <TableHeadCell sort={'name'}>{translate('NAME')}</TableHeadCell>
          <TableHeadCell sort={'fileVersionNo'}>{translate('VERSION')}</TableHeadCell>
          <TableHeadCell sort={'workflow'}>{translate('CHANGE_REQUEST')}</TableHeadCell>
          <TableHeadCell sort={'updatedAt'}>{translate('TABLE_TH_UPDATED_ON')}</TableHeadCell>
          <TableHeadCell id="58ae1e98-0ab7-4765-94b0-6dcde9abe032" className={'actions'}>
            {translate('ACTIONS')}
          </TableHeadCell>
        </TableHeadRow>
      }
      Row={(props: { row: FilePlanItem; [key: string]: any }) => {
        const fsi = props.row
        return (
          <TableRow {...props}>
            <td className="with-thumb clipped-cell">
              <div className="d-flex align-left">
                <div className="ellipsis" title={fsi.name}>
                  <DocIcon fsi={fsi as any} />
                  {(!fsi.id || fsi.type === FsiType.PLACEHOLDER || fsi.acl?.level === AclLevel.BROWSE) && (
                    <span title={fsi.name}>{fsi.name}</span>
                  )}
                  {fsi.type !== FsiType.PLACEHOLDER && fsi.id && fsi.acl?.level !== AclLevel.BROWSE && (
                    <TextLinkButton
                      action={() => {
                        navigate(`/show-file/${fsi.id}`)
                      }}
                    >
                      {fsi.name}
                    </TextLinkButton>
                  )}
                  {fsi.id && <DocState fsi={fsi as any} />}
                </div>
              </div>
            </td>
            <td>{fsi.fileVersionNo ? fsi.fileVersionNo : translate('NOT_AVAILABLE')}</td>
            <td>
              {!fsi.workflowNo ? (
                translate('NOT_AVAILABLE')
              ) : access.isAllowed('workflowsAccessDetail') ? (
                <TextLinkButton
                  action={() => {
                    console.log('Show workflow:', fsi.workflowId)
                  }}
                >
                  {fsi.workflowNo}
                </TextLinkButton>
              ) : (
                fsi.workflowNo
              )}
            </td>
            <td>{formatDateTime(fsi.updatedAt)}</td>
            <td className={'actions'}>
              <FilePlansTableMenu item={fsi} />
            </td>
          </TableRow>
        )
      }}
    />
  )

  const renderPackagesTable = () => (
    <RcmTable
      className="chita-table"
      onRowEnter={(fsi: Package) => {
        console.log('Package clicked:', fsi.id)
      }}
      HeadRow={
        <TableHeadRow className={'has-actions'}>
          <TableHeadCell sort={'name'}>{translate('NAME')}</TableHeadCell>
          <TableHeadCell sort={'level'}>{translate('EMTF_LEVEL')}</TableHeadCell>
          <TableHeadCell sort={'filePlan'}>{translate('FILE_PLAN')}</TableHeadCell>
          {eventTriggers?.length ? (
            <TableHeadCell sort={'autoApplyPackage'}>{translate('AUTO_APPLY_PACKAGE')}</TableHeadCell>
          ) : null}
          <TableHeadCell sort={'createdAt'}>{translate('REPORTS_TABLE_TH_CREATED_ON')}</TableHeadCell>
          <TableHeadCell sort={'updatedAt'}>{translate('TABLE_TH_UPDATED_ON')}</TableHeadCell>
          <TableHeadCell id="58ae1e98-0ab7-4765-94b0-6dcde9abe032" className={'actions'}>
            {translate('ACTIONS')}
          </TableHeadCell>
        </TableHeadRow>
      }
      Row={(props: { row: Package; [key: string]: any }) => {
        const fsi = props.row
        return (
          <TableRow {...props}>
            <td className="clipped-cell">
              <div className="d-flex align-left">
                <TextLinkButton
                  className="ellipsis"
                  title={fsi.name}
                  action={() => {
                    showModal(<EditPackageModal id={fsi?.id} onClose={refreshAllData} />)
                  }}
                >
                  {fsi.name}
                </TextLinkButton>
              </div>
            </td>
            <td>{translate(fsi.level ? 'EMTF_MD_' + fsi.level : 'NOT_AVAILABLE')}</td>
            <td>{fsi.filePlanName ? fsi.filePlanName : translate('NOT_AVAILABLE')}</td>
            {eventTriggers?.length ? (
              <td>
                {fsi?.triggerDetails && Object.keys(fsi.triggerDetails)?.length ? (
                  <div
                    title={
                      fsi.triggerDetails?.triggerDetail?.attributeName
                        ? `${translate('AUTO_APPLY_PACKAGE_TRIGGER')}: ${translate(`TRIGGER_${fsi.triggerDetails.triggerDetail.attributeName}`)}`
                        : ''
                    }
                    className="auto-apply-package-trigger-enable"
                  />
                ) : null}
              </td>
            ) : null}
            <td>{formatDateTime(fsi.createdAt)}</td>
            <td>{formatDateTime(fsi.updatedAt)}</td>
            <td className={'actions'}>
              <ActionsMenu
                menuId={`package-action-${fsi.id}`}
                items={getPackagesActions({
                  item: fsi,
                  onEdit: () => {
                    showModal(<EditPackageModal id={fsi.id} onClose={refreshAllData} />)
                  },
                  onRemove: () =>
                    showModal(
                      <ModalConfirmation
                        cancelTxt={translate('NO')}
                        okTxt={translate('YES')}
                        titleMsg={translate('ETMF_PACKAGES_DELETE_TITLE')}
                        msg={<HtmlCell>{translate('ETMF_PACKAGES_DELETE_TXT') + ' <b>' + fsi.name + '</b>'}</HtmlCell>}
                        onSubmit={() => {
                          removePackage(fsi.id).then(
                            () => {
                              tableData.refreshTable()
                              context.service.toast.success('ETMF_PACKAGES_DELETE_SUCCESS_MSG')
                            },
                            (data) => {
                              context.service.toast.error(data.data.message)
                            }
                          )
                        }}
                        onCancel={() => {
                          if (document.body.classList.contains('modal-open')) {
                            document.body.className = document.body.className.replace(/ ?modal-open/, '')
                          }
                        }}
                      />
                    ),
                  onExport: () => {
                    context.api.settings.exportSettingsData(
                      context.constant.etmf.PACKAGES,
                      () => {},
                      fsi?.id,
                      translate
                    )
                  },
                  onActivity: () => {
                    navigate(`/reports/activity?type=PACKAGES&id=${fsi.id}&itemName=${fsi.name}`)
                  },
                  onClone: () => {
                    showModal(<EditPackageModal id={fsi.id} isClone onClose={refreshAllData} />)
                  },
                  onApplyPkg: () => {
                    showModal(<ApplyPackageModal id={fsi.id} />)
                  }
                })}
              />
            </td>
          </TableRow>
        )
      }}
    />
  )

  return (
    <div className="page-frame">
      <div className="row" style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
        <div className="col-xs-6" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <h1 id="612191fd-ab3e-48f4-bca5-2c40b4b83119" style={{ margin: 0, whiteSpace: 'nowrap' }}>
            {translate(pageTexts.title)}
          </h1>
        </div>
        <div
          className="col-xs-6"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px' }}
        >
          {showPageMenu && (
            <EtmfPageMenu
              actions={pageTexts}
              listView={listView}
              onPackageChange={listView === EtmfListView.PACKAGE ? refreshAllData : undefined}
            />
          )}
          <RcmTableFilterTrigger />
        </div>
      </div>

      <div className="page-content" style={{ position: 'relative' }}>
        {isRefreshing && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}
          >
            <LoadingSpinner size={40} centered />
          </div>
        )}
        <EtmfListFilter ref={filterRef} listView={listView} pageTexts={pageTexts} />
        {listView === EtmfListView.PACKAGE
          ? renderPackagesTable()
          : listView === EtmfListView.FILE_PLAN
            ? renderFilePlansTable()
            : renderMDFTable()}
      </div>
    </div>
  )
}
